import { useCallback, useState } from "react";
import { useTranslation } from "../lib/i18n";
import { useSeo } from "../lib/seo";
import { Alert, AlertTitle, AlertDescription } from "../components/ui/alert";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import * as L from "leaflet";
import "leaflet/dist/leaflet.css";

const markerIcon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

export default function ContactUsPage() {
  const { t, lang } = useTranslation();
  useSeo({ title: t("seoContactTitle"), description: t("seoContactDesc"), path: "/contact", lang });
  const [alert, setAlert] = useState<{
    variant: "destructive" | "default";
    title: string;
    message: string;
  } | null>(null);

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const payload = Object.fromEntries(formData.entries());

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => null);

      if (res.ok) {
        setAlert({
          variant: "default",
          title: t("contactThanks"),
          message: data?.message || t("contactThanks"),
        });
        form.reset();
      } else if (data?.demo || res.status === 503) {
        setAlert({
          variant: "destructive",
          title: t("contactError"),
          message: t("contactDemoError"),
        });
      } else {
        setAlert({
          variant: "destructive",
          title: t("contactError"),
          message: data?.message || t("contactError"),
        });
      }
    } catch (err) {
      console.error(err);
      setAlert({
        variant: "destructive",
        title: t("contactError"),
        message: t("contactError"),
      });
    }
  }, [t]);

  // Approximate coordinates for Šiauriečių aklg., Kaunas
  const position: [number, number] = [54.933, 23.959];

  return (
    <div>
      <h1 className="sr-only">{t("contact")}</h1>
      <section className="w-full">
        <div className="map-container w-full h-80 md:h-96">
          <MapContainer center={position} zoom={15} scrollWheelZoom={false} className="w-full h-full">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker position={position} icon={markerIcon}>
              <Popup>
                UAB „TRANSRENTUS" <br /> Šiauriečių aklg. 17, Kaunas
              </Popup>
            </Marker>
          </MapContainer>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
            <div className="md:col-span-2">
              <h2 className="text-center text-2xl font-semibold mb-4">{t("contactTitle")}</h2>
              <p className="text-center text-muted-foreground mb-8">{t("contactSubtitle")}</p>

              {alert && (
                <div className="mb-6">
                  <Alert variant={alert.variant}>
                    <AlertTitle>{alert.title}</AlertTitle>
                    <AlertDescription>{alert.message}</AlertDescription>
                  </Alert>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6 bg-transparent">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input name="name" required placeholder={t("contactName")} className="h-12 px-4 border rounded bg-white/0" />
                  <input name="email" required type="email" placeholder={t("contactEmail")} className="h-12 px-4 border rounded bg-white/0" />
                </div>

                <input name="subject" placeholder={t("contactSubject")} required className="w-full h-12 px-4 border rounded bg-white/0" />

                <textarea name="message" placeholder={t("contactMessage")} required rows={8} className="w-full p-4 border rounded bg-white/0 resize-vertical" />

                <div className="flex">
                  <button type="submit" className="bg-red-600 text-white px-6 py-2 rounded">{t("contactSubmit")}</button>
                </div>
              </form>
            </div>

            <div>
              <div className="p-4">
                <h3 className="text-lg font-semibold text-red-700 mb-4">{t("contactRequisites")}</h3>

                <p className="mb-2">
                  <strong>UAB „TRANSRENTUS"</strong> - {t("contactDesc")}
                </p>

                <p className="mb-2"><span className="font-medium text-red-700">{t("contactMobile")}</span> +370 698 82999; +370 605 88874</p>
                <p className="mb-2"><span className="font-medium text-red-700">{t("contactTelFax")}</span> +370 37 204404</p>

                <p className="mb-2"><span className="font-medium text-red-700">{t("contactRegAddress")}</span> V. Landsbergio-Žemkalnio 14-1, Kaunas</p>
                <p className="mb-2"><span className="font-medium text-red-700">{t("contactOfficeAddress")}</span> Šiauriečių aklg. 17, Kaunas</p>

                <p className="mt-4 text-sm">{t("contactCompanyCode")} 135938833<br /> {t("contactVatCode")} LT359388314</p>

                <p className="mt-4 text-sm">info@transrentus.lt<br />transrentus@transrentus.lt</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
